import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { LogoUploader } from "./LogoUploader";
import type { TestimonialItem } from "./useTestimonialsManager";

interface FieldProps {
  editingTestimonial: Partial<TestimonialItem>;
  onChange: (key: keyof TestimonialItem, value: any) => void;
}

function AuthorFields({ editingTestimonial, onChange }: FieldProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="author_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Author Name (English) <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="author_en"
          placeholder="e.g. Jane Doe"
          rows={1}
          value={editingTestimonial.author_en || ""}
          onChange={(e) => onChange("author_en", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="author_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Author Name (Turkish)
        </Label>
        <Textarea
          id="author_tr"
          placeholder="Örn. Jane Doe"
          rows={1}
          value={editingTestimonial.author_tr || ""}
          onChange={(e) => onChange("author_tr", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>
    </div>
  );
}

function RoleFields({ editingTestimonial, onChange }: FieldProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="role_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Role/Title (English) <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="role_en"
          placeholder="e.g. Product Manager"
          rows={1}
          value={editingTestimonial.role_en || ""}
          onChange={(e) => onChange("role_en", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="role_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Role/Title (Turkish)
        </Label>
        <Textarea
          id="role_tr"
          placeholder="Örn. Ürün Yöneticisi"
          rows={1}
          value={editingTestimonial.role_tr || ""}
          onChange={(e) => onChange("role_tr", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>
    </div>
  );
}

function CompanyFields({ editingTestimonial, onChange }: FieldProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="company_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Company (English) <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="company_en"
          placeholder="e.g. Tech Corp"
          rows={1}
          value={editingTestimonial.company_en || ""}
          onChange={(e) => onChange("company_en", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="company_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Company (Turkish)
        </Label>
        <Textarea
          id="company_tr"
          placeholder="Örn. Tech Corp"
          rows={1}
          value={editingTestimonial.company_tr || ""}
          onChange={(e) => onChange("company_tr", e.target.value)}
          className="border-border/60 font-sans resize-y leading-normal min-h-[38px] py-2"
        />
      </div>
    </div>
  );
}

function QuoteFields({ editingTestimonial, onChange }: FieldProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label htmlFor="quote_en" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Quote (English) <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="quote_en"
          placeholder="Enter full quote here..."
          rows={4}
          value={editingTestimonial.quote_en || ""}
          onChange={(e) => onChange("quote_en", e.target.value)}
          className="border-border/60 font-sans resize-y leading-relaxed"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="quote_tr" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Quote (Turkish)
        </Label>
        <Textarea
          id="quote_tr"
          placeholder="Örn. Buraya Türkçe yorumu girin..."
          rows={4}
          value={editingTestimonial.quote_tr || ""}
          onChange={(e) => onChange("quote_tr", e.target.value)}
          className="border-border/60 font-sans resize-y leading-relaxed"
        />
      </div>
    </div>
  );
}

interface AvatarFieldsProps extends FieldProps {
  logoUrl?: string;
}

function AvatarFields({ editingTestimonial, onChange, logoUrl }: AvatarFieldsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <LogoUploader
        label="Client/Colleague Avatar"
        logoUrl={logoUrl}
        onChange={(base64) => onChange("avatar_url", base64)}
        onRemove={() => onChange("avatar_url", "")}
      />

      <div className="space-y-1.5">
        <Label htmlFor="sort_order" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Sort Order
        </Label>
        <Input
          id="sort_order"
          type="number"
          value={editingTestimonial.sort_order === undefined ? "" : editingTestimonial.sort_order}
          onChange={(e) => onChange("sort_order", parseInt(e.target.value) || 0)}
          className="border-border/60 font-sans max-w-[150px]"
        />
      </div>
    </div>
  );
}

interface TestimonialFormFieldsProps {
  editingTestimonial: Partial<TestimonialItem>;
  onChange: (key: keyof TestimonialItem, value: any) => void;
}

export function TestimonialFormFields({ editingTestimonial, onChange }: TestimonialFormFieldsProps) {
  return (
    <div className="space-y-5">
      <AuthorFields editingTestimonial={editingTestimonial} onChange={onChange} />
      <RoleFields editingTestimonial={editingTestimonial} onChange={onChange} />
      <CompanyFields editingTestimonial={editingTestimonial} onChange={onChange} />
      <QuoteFields editingTestimonial={editingTestimonial} onChange={onChange} />
      <AvatarFields
        editingTestimonial={editingTestimonial}
        onChange={onChange}
        logoUrl={editingTestimonial.avatar_url}
      />
    </div>
  );
}

export default TestimonialFormFields;
